import { FilterStorage, FilterTypeEnum } from '@bytel/product-wall/types';

import { WallConfigType, WallType } from '@app-types/config';
import { MobilePlanType } from '@app-types/plan';
import { Product } from '@app-types/product';

import { formatTagText, sendTracking } from '@helpers/tracking';

import { PlanTypeId } from '@constants/plan';

const PLAN_TAG_NAMES: Record<string, string> = {
    [PlanTypeId.SOWO]: 'B&You',
    [PlanTypeId.SOWO_PRO]: 'B&You',

    [PlanTypeId.SIMO]: 'Sensation_sim_seule',
    [PlanTypeId.SIMO_PRO]: 'Sensation_sim_seule',

    [PlanTypeId.PREMIUM]: 'Sensation_avantages_smartphone',
    [PlanTypeId.PREMIUM_PRO]: 'Sensation_avantages_smartphone',
};

export function sendFilterTracking(
    wallConfig: WallConfigType,
    isRenewal: boolean,
    label: string,
    value: string,
    checked: boolean,
) {
    const filterData = wallConfig.filters.find((filter) => filter.key === label);

    if (filterData === undefined) {
        return;
    }

    let name: string;
    if (filterData.storage !== FilterStorage.QUERY) {
        const checkedText = checked ? 'coche' : 'decoche';
        if (filterData.type === FilterTypeEnum.CHECKBOX_LIST) {
            const valueData = filterData?.values?.find((currentValue) => currentValue.value === value);
            if (valueData === undefined) {
                return;
            }

            name = formatTagText(`${valueData.label}_${checkedText}`);
        } else {
            name = formatTagText(`${filterData.label}_${checkedText}`);
        }
    } else {
        name = formatTagText(`Prix_max_${value}`);
    }

    const category =
        filterData.label === 'Mode de livraison' ? 'Click_and_collect' : formatTagText('Filtre_' + filterData.label);

    sendTracking(isRenewal, {
        action: 'Clic_selection',
        category,
        name,
    });
}

export function sendResetFilterTracking(isRenewal: boolean) {
    sendTracking(isRenewal, {
        action: 'Clic_selection',
        category: 'Filtre',
        name: 'Tout_decocher',
    });
}

export function sendSortTracking(wallConfig: WallConfigType, isRenewal: boolean, sort: string) {
    let sortLabel = wallConfig.sorter[sort]?.label ?? '';
    sortLabel = sortLabel.charAt(0).toUpperCase() + sortLabel.substring(1).toLowerCase();

    sendTracking(isRenewal, {
        action: 'Clic_selection',
        category: 'Tri',
        name: formatTagText(`Trier_par_${sortLabel}`),
    });
}

export function sendPriceDetailTracking(isRenewal: boolean, type: WallType) {
    sendTracking(isRenewal, {
        action: 'Clic_affichage',
        category: type === WallType.HOTSPOT ? 'Vignette_hotspot' : 'Vignette_telephone',
        name: 'icone_information',
    });
}

export function sendAddToComparatorTracking(isRenewal: boolean) {
    sendTracking(isRenewal, {
        action: 'Clic_selection',
        category: 'Vignette_telephone',
        name: 'Ajouter_au_comparateur',
    });
}

export function sendRemoveFromComparatorTracking(isRenewal: boolean) {
    sendTracking(isRenewal, {
        action: 'Clic_selection',
        category: 'Vignette_telephone',
        name: 'Supprimer_tel_comparaison',
    });
}

export function sendResetComparatorTracking(isRenewal: boolean) {
    sendTracking(isRenewal, {
        action: 'Clic_affichage',
        category: 'Comparaison_telephone',
        name: 'Supprimer_tous_tels_comparaison',
    });
}

export function sendToggleComparatorTracking(isRenewal: boolean, opened: boolean) {
    sendTracking(isRenewal, {
        action: 'Clic_affichage',
        category: 'Comparaison_telephone',
        name: opened ? 'Voir_le_comparateur' : 'Masquer_le_comparateur',
    });
}

export function sendRedirectToComparatorTracking(isRenewal: boolean) {
    sendTracking(isRenewal, {
        action: 'Clic_affichage',
        category: 'Comparaison_telephone',
        name: 'Lancer_comparaison',
    });
}

export function sendRecommendedProductTracking(isRenewal: boolean, isPremium: boolean, product: Product) {
    const category = isPremium ? 'Recommandation_sensation' : 'Recommandation_sowo';

    const name = formatTagText(`${product.id}_${product.name}`);

    sendTracking(isRenewal, {
        action: 'Impressions',
        category,
        name,
    });
}

export function sendPageChangeTracking(isRenewal: boolean) {
    sendTracking(isRenewal, {
        action: 'Clic_redirection',
        category: 'Pagination',
        name: `Changement_de_page`,
    });
}

export function sendPlanDetailsTracking(plan: MobilePlanType) {
    sendTracking(false, {
        category: 'Fiche_produit_mobile',
        eCommerceStep: 'Fiche_produit',
        marketLine: 'Mobile',
        page: formatTagText(`Forfait_${plan.name}`),
        products: [
            {
                name: plan.name,
                id: plan.entityId ?? '',
                gencode: plan.gencode,
                sku: plan.gencode ?? '',
                price: plan.price,
                ligneProduitNiv1: 'Forfaits_mobile',
                gamme: PLAN_TAG_NAMES[plan.typeId] ?? '',
                promo: [],
                type_id: plan.typeId,
            },
        ],
    });
}

export function sendLockPlanTracking(isRenewal: boolean, isLocked: boolean) {
    sendTracking(isRenewal, {
        action: 'Clic_affichage',
        category: 'Mur_forfait_bloque',
        name: `icone_forfait_bloque_${isLocked ? 'coche' : 'decoche'}`,
        eCommerceStep: 'Fiche_produit',
    });
}
